import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { completeSignIn } from "~/auth/oidc";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";

/**
 * /callback — where the identity provider returns. Exchanges the authorization code for a
 * token, then restores wherever the user was heading before sign-in.
 */
export function CallbackScene() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle(t("Signing in"));

  useEffect(() => {
    let cancelled = false;

    completeSignIn(window.location.search)
      .then((returnTo) => {
        if (!cancelled) {
          // A full replace rather than a client-side navigate: it drops the code-bearing URL
          // from history AND guarantees the app boots with the stored token, instead of
          // reusing a router context created while signed out.
          window.location.replace(returnTo);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : t("Sign-in failed"));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  if (error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 px-4 text-center">
        <Text as="h1" variant="h3">
          {t("Sign-in failed")}
        </Text>
        <Text variant="secondary">{error}</Text>
        <Button variant="primary" onClick={() => void navigate({ to: "/login", replace: true })}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3">
      <Spinner label={t("Signing in")} />
      <Text variant="secondary">{t("Completing sign-in…")}</Text>
    </div>
  );
}
